import React, { useState, useEffect } from 'react';
import {
  MdPerson,
  MdSwapHoriz,
  MdBusiness,
  MdLocationOn,
  MdGroup,
  MdVerifiedUser,
  MdTrendingUp,
  MdAccountBalance,
  MdDevices,
  MdReceipt
} from 'react-icons/md';
import UserTypesChart from './UserTypesChart';
import MmdaZoneGroupChart from './MmdaZoneGroupChart';
import TransactionChart from './TransactionChart';
import ChartModal from './ChartModal';
import SupportChartModal from './SupportChartModal';

const DashboardView = ({ dashboardData, error, userTypesData, userInfo, API_BASE_URL, auth }) => {
  const [todayTransactionData, setTodayTransactionData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isChartModalOpen, setIsChartModalOpen] = useState(false);
  const [isSupportChartModalOpen, setIsSupportChartModalOpen] = useState(false);

  useEffect(() => {
    const fetchTodayTransactions = async () => {
      try {
        const response = await fetch('/api/transactions');
        
        // Check if response is ok
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        // Check content type
        const contentType = response.headers.get('content-type');
        if (!contentType || !contentType.includes('application/json')) {
          throw new Error('Invalid response format');
        }
        
        const allTransactions = await response.json();
        
        // Get today's date in YYYY-MM-DD format
        const today = new Date();
        const todayStr = today.toISOString().split('T')[0];
        
        // Filter transactions for today using transaction_time
        const todayTransactions = allTransactions.filter(transaction => {
          if (!transaction.transaction_time) return false;
          const transactionDate = new Date(transaction.transaction_time).toISOString().split('T')[0];
          return transactionDate === todayStr;
        });
        
        // Create simple count for the pie chart
        const todayCount = todayTransactions.length;
        
        setTodayTransactionData([
          { name: 'Today\'s Transactions', count: todayCount || 0 }
        ]);
      } catch (err) {
        console.error('Error fetching today\'s transactions:', err);
        setTodayTransactionData([
          { name: 'Today\'s Transactions', count: 0 }
        ]);
      } finally {
        setLoading(false);
      }
    };

    fetchTodayTransactions();
  }, []);

  // Use today's transaction count for the pie chart
  const transactionChartData = todayTransactionData;

  return (
    <div style={{ padding: '0px' }}>
      {error && (
        <div style={{
          marginBottom: 20,
          padding: '12px 16px',
          backgroundColor: '#ffebee',
          color: '#c62828',
          borderRadius: 8,
          border: '1px solid #ffcdd2',
          fontWeight: '500'
        }}>
          Error: {error}
        </div>
      )}

      {/* System Overview Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 20, marginBottom: '30px' }}>
        {/* Users Card */}
        <div style={{
          backgroundColor: '#fff',
          padding: 24,
          borderRadius: 12,
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
          display: 'flex',
          alignItems: 'center',
          gap: 16,
          border: '1px solid #f0f0f0',
          transition: 'transform 0.2s ease, box-shadow 0.2s ease',
          cursor: 'pointer'
        }}
        onMouseEnter={e => {
          e.currentTarget.style.transform = 'translateY(-2px)';
          e.currentTarget.style.boxShadow = '0 8px 20px rgba(0,0,0,0.15)';
        }}
        onMouseLeave={e => {
          e.currentTarget.style.transform = 'translateY(0)';
          e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)';
        }}>
          <div style={{
            backgroundColor: '#e3f2fd',
            padding: 16,
            borderRadius: 12,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <MdPerson size={32} style={{ color: '#1976d2' }} />
          </div>
          <div>
            <h3 style={{ margin: '0 0 8px 0', fontSize: 18, color: '#333', fontWeight: '600' }}>System Users</h3>
            <p style={{ margin: 0, fontSize: 28, fontWeight: 'bold', color: '#1976d2', lineHeight: 1 }}>
              {dashboardData.usersCount || 0}
            </p>
            <p style={{ margin: '8px 0 0 0', fontSize: 13, color: '#666', fontWeight: '500' }}>
              Total registered users
            </p>
          </div>
        </div>

        {/* Transactions Card */}
        <div style={{
          backgroundColor: '#fff',
          padding: 24,
          borderRadius: 12,
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
          display: 'flex',
          alignItems: 'center',
          gap: 16,
          border: '1px solid #f0f0f0',
          transition: 'transform 0.2s ease, box-shadow 0.2s ease',
          cursor: 'pointer'
        }}
        onMouseEnter={e => {
          e.currentTarget.style.transform = 'translateY(-2px)';
          e.currentTarget.style.boxShadow = '0 8px 20px rgba(0,0,0,0.15)';
        }}
        onMouseLeave={e => {
          e.currentTarget.style.transform = 'translateY(0)';
          e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)';
        }}>
          <div style={{
            backgroundColor: '#e8f5e8',
            padding: 16,
            borderRadius: 12,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <MdSwapHoriz size={32} style={{ color: '#2e7d32' }} />
          </div>
          <div>
            <h3 style={{ margin: '0 0 8px 0', fontSize: 18, color: '#333', fontWeight: '600' }}>Transactions</h3>
            <p style={{ margin: 0, fontSize: 28, fontWeight: 'bold', color: '#2e7d32', lineHeight: 1 }}>
              {dashboardData.transactionsCount || 0}
            </p>
            <p style={{ margin: '8px 0 0 0', fontSize: 13, color: '#666', fontWeight: '500' }}>
              Today: {todayTransactionData.reduce((sum, item) => sum + item.count, 0)}
            </p>
          </div>
        </div>

        {/* MMDAs Card */}
        <div style={{
          backgroundColor: '#fff',
          padding: 24,
          borderRadius: 12,
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
          display: 'flex',
          alignItems: 'center',
          gap: 16,
          border: '1px solid #f0f0f0',
          transition: 'transform 0.2s ease, box-shadow 0.2s ease',
          cursor: 'pointer'
        }}
        onMouseEnter={e => {
          e.currentTarget.style.transform = 'translateY(-2px)';
          e.currentTarget.style.boxShadow = '0 8px 20px rgba(0,0,0,0.15)';
        }}
        onMouseLeave={e => {
          e.currentTarget.style.transform = 'translateY(0)';
          e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)';
        }}>
          <div style={{
            backgroundColor: '#fff3e0',
            padding: 16,
            borderRadius: 12,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <MdBusiness size={32} style={{ color: '#f57c00' }} />
          </div>
          <div>
            <h3 style={{ margin: '0 0 8px 0', fontSize: 18, color: '#333', fontWeight: '600' }}>MMDAs</h3>
            <p style={{ margin: 0, fontSize: 28, fontWeight: 'bold', color: '#f57c00', lineHeight: 1 }}>
              {dashboardData.mmdasCount || 0}
            </p>
            <p style={{ margin: '8px 0 0 0', fontSize: 13, color: '#666', fontWeight: '500' }}>
              Active assemblies
            </p>
          </div>
        </div>

        {/* Additional Cards for more comprehensive view */}

      </div>

      {/* Chart Actions */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '20px',
        padding: '16px 20px',
        backgroundColor: '#fff',
        borderRadius: '8px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
      }}>
        <h3 style={{ margin: 0, color: '#2c3e50' }}>System Analytics</h3>
        <button
          onClick={() => setIsChartModalOpen(true)}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '10px 16px',
            backgroundColor: '#1976d2',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: '500',
            transition: 'background-color 0.2s ease'
          }}
          onMouseEnter={e => e.currentTarget.style.backgroundColor = '#1565c0'}
          onMouseLeave={e => e.currentTarget.style.backgroundColor = '#1976d2'}
        >
          📊 View Charts
        </button>
      </div>

      {/* Charts Section */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))',
        gap: 24,
        marginBottom: '30px'
      }}>
        {/* User Types Chart */}
        <div style={{
          backgroundColor: '#fff',
          borderRadius: 12,
          padding: 24,
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
          border: '1px solid #f0f0f0'
        }}>
          <div style={{ marginBottom: 20 }}>
            <h3 style={{
              margin: '0 0 8px 0',
              fontSize: 18,
              color: '#333',
              fontWeight: '600',
              display: 'flex',
              alignItems: 'center',
              gap: 10
            }}>
              <MdGroup size={20} style={{ color: '#1976d2' }} />
              User Distribution
            </h3>
            <p style={{ margin: 0, fontSize: 14, color: '#666' }}>
              System users by role type
            </p>
          </div>
          <UserTypesChart userTypesData={userTypesData} />
        </div>

        {/* Transaction Chart */}
        <div style={{
          backgroundColor: '#fff',
          borderRadius: 12,
          padding: 24,
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
          border: '1px solid #f0f0f0'
        }}>
          <div style={{ marginBottom: 20 }}>
            <h3 style={{
              margin: '0 0 8px 0',
              fontSize: 18,
              color: '#333',
              fontWeight: '600',
              display: 'flex',
              alignItems: 'center',
              gap: 10
            }}>
              <MdTrendingUp size={20} style={{ color: '#2e7d32' }} />
              Today's Activity
            </h3>
            <p style={{ margin: 0, fontSize: 14, color: '#666' }}>
              Transaction volume for today
            </p>
          </div>
          {loading ? (
            <div style={{
              padding: '40px 20px',
              textAlign: 'center',
              color: '#666',
              backgroundColor: '#f8f9fa',
              borderRadius: '8px',
              border: '1px solid #e9ecef'
            }}>
              <div style={{ marginBottom: 12 }}>
                <div style={{
                  width: 24,
                  height: 24,
                  border: '3px solid #e9ecef',
                  borderTop: '3px solid #1976d2',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite',
                  margin: '0 auto'
                }} />
              </div>
              Loading today's transactions...
            </div>
          ) : (
            <TransactionChart transactionData={transactionChartData} />
          )}
        </div>

        {/* MMDA Zone Group Chart */}
        <div style={{
          backgroundColor: '#fff',
          borderRadius: 12,
          padding: 24,
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
          border: '1px solid #f0f0f0'
        }}>
          <div style={{ marginBottom: 20 }}>
            <h3 style={{
              margin: '0 0 8px 0',
              fontSize: 18,
              color: '#333',
              fontWeight: '600',
              display: 'flex',
              alignItems: 'center',
              gap: 10
            }}>
              <MdAccountBalance size={20} style={{ color: '#f57c00' }} />
              MMDA Overview
            </h3>
            <p style={{ margin: 0, fontSize: 14, color: '#666' }}>
              Zones and groups distribution
            </p>
          </div>
          <MmdaZoneGroupChart chartData={dashboardData.mmdaZoneGroupData} />
        </div>
      </div>

      <style jsx>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>

      {/* Chart Modal */}
      <ChartModal
        isOpen={isChartModalOpen}
        onClose={() => setIsChartModalOpen(false)}
        userRole="Super Admin"
        userInfo={userInfo}
        dashboardData={dashboardData}
        API_BASE_URL={API_BASE_URL}
        auth={auth}
        onSupportChartOpen={() => setIsSupportChartModalOpen(true)}
      />

      {/* Support Chart Modal */}
      <SupportChartModal
        isOpen={isSupportChartModalOpen}
        onClose={() => setIsSupportChartModalOpen(false)}
        userRole="Super Admin"
        userInfo={userInfo}
        API_BASE_URL={API_BASE_URL}
        auth={auth}
      />

    </div>
  );
};

export default DashboardView;
